import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { createScrapEntry, fetchStationsByLine, type CreateScrapEntryInput } from '@/core/api';
import { useAuth } from '@/core/AuthContext';
import { useFactory } from '@/core/hooks/useFactory';
import { useTenant } from '@/core/hooks/useTenant';
import { useLanguage } from '@/core/LanguageContext';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { ArticleSelector } from '@/shared/components/selectors';
import { useToast } from '@/shared/hooks/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';

interface ScrapEntryFormProps {
  entryDate: string;
  areaId: string;
  lineId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

/**
 * Compact inline form for adding scrap entries.
 */
export const ScrapEntryForm = ({ entryDate, areaId, lineId, onSuccess, onCancel }: ScrapEntryFormProps) => {
  const { user } = useAuth();
  const { selectedFactoryId } = useFactory();
  const { selectedTenantId } = useTenant();
  const { t } = useLanguage();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [lossAmount, setLossAmount] = useState('');
  const [articleId, setArticleId] = useState<string | null>(null);
  const [stationId, setStationId] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [showOptional, setShowOptional] = useState(false);

  const { data: stations = [] } = useQuery({
    queryKey: ['stations-by-line', selectedTenantId, lineId],
    queryFn: () => fetchStationsByLine(lineId),
    enabled: !!lineId,
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateScrapEntryInput) => createScrapEntry(input),
    onSuccess: () => {
      toast({ title: t('core.scrap.entryAdded', 'Scrap entry added') });
      setLossAmount('');
      setArticleId(null);
      setStationId(null);
      setComment('');
      setShowOptional(false);
      queryClient.invalidateQueries({ queryKey: ['scrap-entries'] });
      queryClient.invalidateQueries({ queryKey: ['scrap-entries-area'] });
      queryClient.invalidateQueries({ queryKey: ['scrap-daily-summaries'] });
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({ title: t('core.scrap.error', 'Error'), description: error.message, variant: 'destructive' });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTenantId || !selectedFactoryId) return;

    const amount = parseFloat(lossAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({ title: t('core.scrap.invalidAmount', 'Invalid amount'), variant: 'destructive' });
      return;
    }

    createMutation.mutate({
      tenant_id: selectedTenantId,
      factory_id: selectedFactoryId,
      entry_date: entryDate,
      loss_amount: amount,
      area_id: areaId,
      line_id: lineId,
      station_id: stationId,
      article_id: articleId,
      comment: comment || null,
      created_by: user?.id,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="p-3 bg-muted/30 rounded-lg space-y-2">
      {/* Primary row: Amount + quick actions */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-[140px]">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">€</span>
          <Input
            type="number"
            step="0.01"
            min="0"
            placeholder="0.00"
            value={lossAmount}
            onChange={(e) => setLossAmount(e.target.value)}
            className="pl-7 h-9"
            autoFocus
            required
          />
        </div>
        
        <Input
          placeholder={t('core.scrap.notePlaceholder', 'Note (optional)')}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          className="h-9 flex-1"
        />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-9 text-xs"
          onClick={() => setShowOptional(!showOptional)}
        >
          {showOptional ? t('core.scrap.less', 'Less') : t('core.scrap.more', 'More')}
        </Button>

        <Button type="submit" size="sm" className="h-9" disabled={createMutation.isPending}>
          <Plus className="h-4 w-4 mr-1" />
          {t('core.scrap.add', 'Add')}
        </Button>

        {onCancel && (
          <Button type="button" variant="ghost" size="icon" className="h-9 w-9" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Optional fields row */}
      {showOptional && (
        <div className="flex items-center gap-2 pt-1">
          <div className="flex-1">
            <ArticleSelector
              value={articleId}
              onValueChange={setArticleId}
              areaId={areaId}
              lineId={lineId}
              allowClear
              placeholder={t('core.scrap.article', 'Article')}
            />
          </div>
          <div className="w-[160px]">
            <Select 
              value={stationId || '__NONE__'} 
              onValueChange={(val) => setStationId(val === '__NONE__' ? null : val)}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder={t('core.scrap.station', 'Station')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__NONE__">
                  <span className="text-muted-foreground">{t('core.scrap.noStation', 'No station')}</span>
                </SelectItem>
                {stations.map((station) => (
                  <SelectItem key={station.id} value={station.id}>
                    {station.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </form>
  );
};
